/**
 * SafeImg — wraps <img> with an automatic Picsum fallback on load error.
 *
 * Iter 166 — every <img> rendered in the canvas can now be double-clicked
 * to open the FloatingImageToolbar (shape mask, replace, crop, etc.).
 * We auto-derive blockId from the closest `[data-block-id]` ancestor and
 * use either an explicit `field` prop or a synthetic key based on the
 * image's position within its block. The toolbar's apply path already
 * handles `_image_frames` persistence — once the user picks a shape, the
 * frame is saved against blockId/field and read by the renderer on the
 * next paint.
 */
import React, { useRef, useState, useEffect } from 'react';
import { SHAPES } from './sqs/imageFrameSystem/shapes';

export function SafeImg({ src, alt = '', className = '', style, fallbackSeed, blockId, field, ...rest }) {
  const [errored, setErrored] = useState(false);
  const [autoField, setAutoField] = useState(field || null);
  const [shape, setShape] = useState(null);
  const [focal, setFocal] = useState(null); // Iter 198d — {x, y} from parent section
  const ref = useRef(null);
  const seed = fallbackSeed || (typeof src === 'string' ? src.slice(-40).replace(/[^a-zA-Z0-9]/g, '') : 'wb');
  const fallback = `https://picsum.photos/seed/${seed}/800/600`;
  const finalSrc = errored || !src ? fallback : src;

  // Auto-derive block context from DOM ancestors on mount.
  useEffect(() => {
    if (!ref.current || field) return;
    const blockEl = ref.current.closest('[data-block-id]');
    if (!blockEl) return;
    const imgs = Array.from(blockEl.querySelectorAll('img'));
    const idx = imgs.indexOf(ref.current);
    if (idx >= 0) setAutoField(`_img_${idx}`);
  }, [field]);

  // Live-read the persisted shape from a nearby `data-wb-frames` JSON-encoded
  // attribute (set by the renderer when block._image_frames updates).
  useEffect(() => {
    if (!ref.current) return;
    const blockEl = ref.current.closest('[data-block-id]');
    const fld = field || autoField;
    if (!blockEl || !fld) return;
    const apply = () => {
      const raw = blockEl.getAttribute('data-wb-frames');
      if (!raw) { setShape(null); return; }
      try {
        const frames = JSON.parse(raw);
        const f = frames?.[fld];
        if (f?.shape && SHAPES[f.shape]) setShape(f.shape);
        else setShape(null);
      } catch { setShape(null); }
    };
    apply();
    const obs = new MutationObserver(apply);
    obs.observe(blockEl, { attributes: true, attributeFilter: ['data-wb-frames'] });
    return () => obs.disconnect();
  }, [field, autoField]);

  // Iter 198d — Focal Point Everywhere. SafeImg auto-reads the closest
  // block's `data-wb-focal="X Y"` attribute (written by every renderer via
  // the shared block wrapper in DashboardParts) and applies it as
  // objectPosition so `object-cover` respects the vendor's chosen crop at
  // every aspect ratio. Uses MutationObserver so live focal-point drag in
  // the editor updates instantly.
  useEffect(() => {
    if (!ref.current) return;
    const blockEl = ref.current.closest('[data-block-id], [data-wb-focal]');
    if (!blockEl) return;
    const apply = () => {
      const raw = blockEl.getAttribute('data-wb-focal');
      if (!raw) { setFocal(null); return; }
      const [x, y] = raw.split(' ').map(Number);
      if (Number.isFinite(x) && Number.isFinite(y)) setFocal({ x, y });
      else setFocal(null);
    };
    apply();
    const obs = new MutationObserver(apply);
    obs.observe(blockEl, { attributes: true, attributeFilter: ['data-wb-focal'] });
    return () => obs.disconnect();
  }, []);

  const handleDouble = (e) => {
    e.stopPropagation();
    const el = ref.current;
    const blockEl = el?.closest('[data-block-id]');
    const bid = blockId || blockEl?.getAttribute('data-block-id');
    const fld = field || autoField;
    if (!bid || !fld || !el) return;
    const r = el.getBoundingClientRect();
    window.dispatchEvent(new CustomEvent('wb:edit-image', {
      detail: { blockId: bid, field: fld, rect: { top: r.top, left: r.left, width: r.width, height: r.height } }
    }));
  };

  const clip = shape ? (SHAPES[shape]?.clipPath !== 'none' ? SHAPES[shape].clipPath : undefined) : undefined;
  const focalStyle = focal ? { objectPosition: `${focal.x}% ${focal.y}%` } : null;
  const mergedStyle = { ...(focalStyle || {}), ...(style || {}), ...(clip ? { clipPath: clip } : {}) };

  return (
    <img
      ref={ref}
      src={finalSrc}
      alt={alt}
      className={className}
      style={mergedStyle}
      onError={() => { if (!errored) setErrored(true); }}
      onDoubleClick={handleDouble}
      data-wb-image={blockId || autoField ? `${blockId || ''}/${field || autoField || ''}` : undefined}
      loading="lazy"
      {...rest}
    />
  );
}

export default SafeImg;
